import { useState, useRef, useEffect, useMemo } from "react";
import { createPortal } from "react-dom";
import { motion } from "framer-motion";
import { apiFetch } from "@shared/lib/api";
import { useAuthActions, useAuthUser } from "@shared/lib/auth";
import { simpleMarkdownToHtml } from "@shared/lib/utils";

/**
 * Modal that appears when a staff member's
 * data_use_acknowledgment_version is outdated (or null).
 * Cannot be dismissed without clicking "I Acknowledge".
 */
export function DataUseReacknowledgmentModal() {
  const { user, dataUseAcknowledgment } = useAuthUser();
  const { refreshMe } = useAuthActions();
  const [acknowledging, setAcknowledging] = useState(false);
  const modalRef = useRef<HTMLDivElement>(null);

  // Auto-focus modal on mount
  useEffect(() => {
    if (modalRef.current) {
      modalRef.current.focus();
    }
  }, []);

  // Focus trap — cycle Tab within the modal (no Escape, non-dismissible)
  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === "Tab" && modalRef.current) {
        const focusable = modalRef.current.querySelectorAll<HTMLElement>(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        );
        if (focusable.length === 0) return;

        const first = focusable[0];
        const last = focusable[focusable.length - 1];

        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    }

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, []);

  // Only show for staff members (admin, director, teacher) who haven't acknowledged the current version
  if (!user || !["admin", "director", "teacher"].includes(user.role)) return null;
  if (user.data_use_acknowledged) return null;
  if (!dataUseAcknowledgment?.text) return null;

  const policyHtml = useMemo(() => simpleMarkdownToHtml(dataUseAcknowledgment.text), [dataUseAcknowledgment.text]);

  const handleAcknowledge = async () => {
    setAcknowledging(true);
    try {
      await apiFetch("/api/v1/me/acknowledge_data_use", { method: "PATCH" });
      await refreshMe();
    } catch {
      // Will retry on next click
    } finally {
      setAcknowledging(false);
    }
  };

  return createPortal(
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 z-[70] bg-black/60" />

      {/* Modal */}
      <div className="fixed inset-0 z-[71] flex items-center justify-center p-4">
        <motion.div
          ref={modalRef}
          role="alertdialog"
          aria-modal="true"
          aria-labelledby="data-use-ack-title"
          tabIndex={-1}
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.2, ease: "easeOut" }}
          className="w-[520px] max-w-full bg-surface rounded-2xl shadow-2xl border border-border/50 p-6 outline-none"
        >
          {/* Header */}
          <div className="flex items-center gap-2 mb-4 text-accent">
            <svg aria-hidden="true" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
            </svg>
            <h2 id="data-use-ack-title" className="text-xl font-semibold font-display text-foreground">
              Updated Acceptable Use Policy
            </h2>
          </div>

          <p className="text-sm text-muted mb-4">
            Our acceptable use policy for handling children&apos;s data has been updated.
            Please review and acknowledge the updated terms to continue.
          </p>

          {/* Policy text */}
          <div className="bg-warning-bg border border-warning-text/20 rounded-lg p-4 mb-5 max-h-[40vh] overflow-y-auto">
            <div
              className="text-sm text-warning-text leading-relaxed prose-policy"
              dangerouslySetInnerHTML={{ __html: policyHtml }}
            />
          </div>

          {/* Action */}
          <div className="flex justify-end">
            <button
              onClick={handleAcknowledge}
              disabled={acknowledging}
              className="px-6 py-2.5 text-sm font-semibold text-accent-foreground bg-accent rounded-lg transition-all hover:-translate-y-0.5 disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {acknowledging ? "Saving..." : "I Acknowledge"}
            </button>
          </div>
        </motion.div>
      </div>
    </>,
    document.body
  );
}
